const { applyAttendanceTimestamp } = require('./lessonAttendance');
const SUPPORTED_AUTH_ROLES = ["ADMIN", "OPERATOR", "TEACHER", "CLIENT"];
const SUPPORTED_MANAGEMENT_ROLES = ["ADMIN", "OPERATOR", "TEACHER"];
const LESSON_SUMMARY_MAX_LENGTH = 600;

function normalizeLessonSummary(value) {
  if (value === undefined || value === null) return "";
  if (typeof value !== "string") throw new Error("Lesson summary must be a string");

  const normalized = value.trim();
  if ([...normalized].length > LESSON_SUMMARY_MAX_LENGTH) {
    throw new Error(`Lesson summary cannot exceed ${LESSON_SUMMARY_MAX_LENGTH} characters`);
  }
  return normalized;
}

const ROLE_PERMISSIONS = {
  ADMIN: ["*"],
  OPERATOR: [
    "clients.read", "clients.create", "clients.update", "clients.delete",
    "students.read", "students.create", "students.update", "students.delete",
    "teachers.read",
    "books.read", "books.create", "books.update", "books.delete",
    "classes.read", "classes.create", "classes.update", "classes.delete",
    "classStudents.read", "classStudents.create", "classStudents.delete",
    "attendance.read", "attendance.update", "attendance.delete",
    "payments.read", "payments.update",
    "settings.read", "settings.update",
    "schoolCalendar.read", "schoolCalendar.manage",
    "reports.read",
  ],
  CLIENT: ["schoolCalendar.read"],
  TEACHER: [
    "schoolCalendar.read",
    "teachers.read",
    "settings.read",
    "classes.read",
    "classStudents.read",
    "students.read",
    "attendance.read",
    "attendance.update",
    "reports.read",
  ],
};

function apiError(statusCode, message) {
  return Object.assign(new Error(message), { statusCode });
}

function normalizeRole(value) {
  if (!value) return null;
  const normalized = String(value).toUpperCase();
  return SUPPORTED_AUTH_ROLES.includes(normalized) ? normalized : null;
}

function normalizeManagementRole(value) {
  const normalized = normalizeRole(value);
  return SUPPORTED_MANAGEMENT_ROLES.includes(normalized) ? normalized : null;
}

function parseAuthorizerRoles(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function normalizeRoleList(value) {
  return parseAuthorizerRoles(value).map(normalizeManagementRole).filter(Boolean);
}

function normalizeAuthContext(event = {}) {
  const authorizer = (event.requestContext && event.requestContext.authorizer) || {};
  const legacyRoles = parseAuthorizerRoles(authorizer.roles);
  const legacyRole = legacyRoles.length === 1 ? normalizeRole(legacyRoles[0]) : null;
  const role = normalizeRole(authorizer.role) || legacyRole;

  const systemRoles = normalizeRoleList(authorizer.systemRoles);
  const platformRoles = [...new Set(parseAuthorizerRoles(authorizer.platformRoles).filter(value => value === 'SUPER_ADMIN'))];
  return {
    id: authorizer.id || null,
    email: authorizer.email || authorizer.id || null,
    role,
    teacherId: authorizer.teacherId || null,
    clientId: authorizer.clientId || null,
    schoolId: authorizer.schoolId || null,
    schoolName: authorizer.schoolName || null,
    activeContextKey: authorizer.activeContextKey || null,
    ...(systemRoles.length > 0 ? { systemRoles } : {}),
    ...(platformRoles.length > 0 ? { platformRoles } : {}),
  };
}

function getRolePermissions(role) {
  return ROLE_PERMISSIONS[normalizeRole(role)] || [];
}

function hasPermission(auth = {}, permission) {
  const roles = [
    auth.role,
    ...(Array.isArray(auth.systemRoles) ? auth.systemRoles : []),
  ];
  const grants = roles.flatMap(getRolePermissions);
  return grants.includes("*") || grants.includes(permission);
}

function hasAnyRole(auth = {}, roles = []) {
  const normalizedRoles = roles.map(normalizeManagementRole).filter(Boolean);
  return [
    auth.role,
    ...(Array.isArray(auth.systemRoles) ? auth.systemRoles : []),
  ].map(normalizeManagementRole).some(role => normalizedRoles.includes(role));
}

function isTeacherScoped(auth = {}) {
  return auth.role === "TEACHER" && !hasAnyRole(auth, ["ADMIN", "OPERATOR"]);
}

function requirePermission(auth, permission) {
  if (!auth || !auth.id || !auth.role) {
    throw apiError(401, "Unauthorized");
  }

  if (!hasPermission(auth, permission)) {
    throw apiError(403, "Forbidden");
  }
}

function requireAdmin(auth) {
  if (!auth || !auth.id || !hasAnyRole(auth, ["ADMIN"])) {
    throw apiError(403, "Forbidden");
  }
}

function requireTeacherIdentity(auth) {
  if (!auth || auth.role !== "TEACHER" || !auth.teacherId) {
    throw apiError(403, "Forbidden");
  }
}

function assertTeacherLessonRegisterWritePayload(payload = {}) {
  const allowedTopLevel = ["id", "lessonId", "lessonDetails", "students"];
  const allowedLessonDetails = ["comment", "lessonSummary"];
  const allowedStudentKeys = ["id", "isPresent"];

  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw apiError(403, "Forbidden");
  }

  if (Object.keys(payload).some(key => !allowedTopLevel.includes(key))) {
    throw apiError(403, "Forbidden");
  }

  const lessonDetails = payload.lessonDetails || {};
  if (lessonDetails && (typeof lessonDetails !== "object" || Array.isArray(lessonDetails))) {
    throw apiError(403, "Forbidden");
  }
  if (Object.keys(lessonDetails).some(key => !allowedLessonDetails.includes(key))) {
    throw apiError(403, "Forbidden");
  }
  if (Object.prototype.hasOwnProperty.call(lessonDetails, "lessonSummary")) {
    try {
      normalizeLessonSummary(lessonDetails.lessonSummary);
    } catch (error) {
      throw apiError(400, error.message);
    }
  }

  const students = payload.students || [];
  if (!Array.isArray(students)) {
    throw apiError(403, "Forbidden");
  }

  if (students.some(student => !student || typeof student !== "object" || Array.isArray(student))) {
    throw apiError(403, "Forbidden");
  }

  const studentKeys = students.flatMap(student => Object.keys(student));
  if (studentKeys.some(key => !allowedStudentKeys.includes(key))) {
    throw apiError(403, "Forbidden");
  }
}

function buildTeacherLessonRegisterPatch(payload = {}) {
  assertTeacherLessonRegisterWritePayload(payload);
  const hasComment = Boolean(
    payload.lessonDetails &&
    Object.prototype.hasOwnProperty.call(payload.lessonDetails, "comment"),
  );
  const hasLessonSummary = Boolean(
    payload.lessonDetails &&
    Object.prototype.hasOwnProperty.call(payload.lessonDetails, "lessonSummary"),
  );

  return {
    lessonDetails: {
      ...(hasComment ? { comment: payload.lessonDetails.comment || "" } : {}),
      ...(hasLessonSummary ? { lessonSummary: normalizeLessonSummary(payload.lessonDetails.lessonSummary) } : {}),
    },
    students: (payload.students || []).map(student => ({
      id: student.id,
      isPresent: !!student.isPresent,
    })),
  };
}

function mergeTeacherLessonRegisterUpdate(existing, patch) {
  const patchByStudentId = (patch.students || []).reduce((acc, student) => ({
    ...acc,
    [student.id]: student,
  }), {});

  const participants = existing.students || [];
  const hasCompleteAttendancePatch = participants.some(student => patchByStudentId[student.id]) &&
    participants.length > 0 && participants.every(student => (
      ["cancelledOnTime", "cancelledLate"].includes(student.participationStatus) ||
      (patchByStudentId[student.id] && typeof patchByStudentId[student.id].isPresent === "boolean")
    ));

  return applyAttendanceTimestamp({
    ...existing,
    recordAttendance: hasCompleteAttendancePatch,
    lessonDetails: {
      ...(existing.lessonDetails || {}),
      ...(hasCompleteAttendancePatch
        ? { attendanceStatus: "checked" }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch.lessonDetails || {}, "comment")
        ? { comment: patch.lessonDetails.comment }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch.lessonDetails || {}, "lessonSummary")
        ? { lessonSummary: patch.lessonDetails.lessonSummary }
        : {}),
    },
    students: (existing.students || []).map(student => (
      patchByStudentId[student.id]
        ? {
          ...student,
          isPresent: patchByStudentId[student.id].isPresent,
          ...(["scheduled", "attended", "absent"].includes(student.participationStatus)
            ? { participationStatus: patchByStudentId[student.id].isPresent ? "attended" : "absent" }
            : {}),
        }
        : student
    )),
  }, existing);
}

function mergeTeacherLessonRegisterCommentUpdate(existing, patch) {
  return {
    ...existing,
    lessonDetails: {
      ...(existing.lessonDetails || {}),
      ...(Object.prototype.hasOwnProperty.call(patch.lessonDetails || {}, "comment")
        ? { comment: patch.lessonDetails.comment }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(patch.lessonDetails || {}, "lessonSummary")
        ? { lessonSummary: patch.lessonDetails.lessonSummary }
        : {}),
    },
  };
}

function isTeacherOwnedClass(auth, schoolClass) {
  return Boolean(auth && auth.teacherId && schoolClass && schoolClass.teacherId === auth.teacherId);
}

function isTeacherSubstitutionLesson(auth, lessonRegister) {
  return Boolean(
    auth &&
    auth.teacherId &&
    lessonRegister &&
    lessonRegister.lessonDetails &&
    lessonRegister.lessonDetails.teacherId === auth.teacherId,
  );
}

function canTeacherAccessLessonRegister(auth, lessonRegister, schoolClass) {
  return isTeacherOwnedClass(auth, schoolClass) || isTeacherSubstitutionLesson(auth, lessonRegister);
}

function canTeacherEditLessonRegisterAttendance(auth, lessonRegister) {
  return isTeacherSubstitutionLesson(auth, lessonRegister);
}

function projectTeacherStudent(student = {}, clientDisplayName = "") {
  return {
    id: student.id,
    firstName: student.firstName || "",
    lastName: student.lastName || "",
    additionalInfo: student.additionalInfo || "",
    clientId: student.clientId || "",
    clientDisplayName,
  };
}

function projectContactProfile(profile = {}) {
  return {
    id: profile.id,
    firstName: profile.firstName || "",
    lastName: profile.lastName || "",
    email: profile.email || "",
    mobile: profile.mobile || "",
    archived: !!profile.archived,
    deleted: !!profile.deleted,
  };
}

function assertTeacherSelfProfileUpdate(auth, id, payload) {
  requireTeacherIdentity(auth);
  if (id !== auth.teacherId || !payload || typeof payload !== "object" || Array.isArray(payload) ||
      (payload.id !== undefined && payload.id !== id) ||
      Object.keys(payload).some(key => !["id", "firstName", "lastName", "email", "mobile"].includes(key))) {
    throw apiError(403, "Forbidden");
  }
}

module.exports = {
  projectContactProfile,
  assertTeacherSelfProfileUpdate,
  LESSON_SUMMARY_MAX_LENGTH,
  ROLE_PERMISSIONS,
  SUPPORTED_MANAGEMENT_ROLES,
  apiError,
  assertTeacherLessonRegisterWritePayload,
  buildTeacherLessonRegisterPatch,
  canTeacherEditLessonRegisterAttendance,
  canTeacherAccessLessonRegister,
  getRolePermissions,
  hasPermission,
  hasAnyRole,
  isTeacherOwnedClass,
  isTeacherSubstitutionLesson,
  isTeacherScoped,
  mergeTeacherLessonRegisterCommentUpdate,
  mergeTeacherLessonRegisterUpdate,
  normalizeAuthContext,
  normalizeLessonSummary,
  normalizeManagementRole,
  normalizeRole,
  projectTeacherStudent,
  requireAdmin,
  requirePermission,
  requireTeacherIdentity,
};
